import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { v4 as uuid } from 'uuid'

import Input from '~/bootcamp/Activities/components/Input'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { NumHistory } from '~/bootcamp/Activities/Multiplication/MultiBy/types'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Plus from '~/bootcamp/Activities/components/Plus'
import { isAllowedValue, hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import MultiplicationActivityWrapper from '~/bootcamp/Activities/Multiplication/components/MultiplicationActivityWrapper'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { difficulty } from './constents'

enum Position {
  TENS = 'tens',
  ONES = 'ones',
}

export default function Distributive({
  answerState,
  onAnswerStateChange,
  onInstanceChange,
  difficultyLevel,
  onNextQuestionClick,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const [tens, setTens] = useState('')
  const [one, setOne] = useState('')
  const [inputKey, setInputKey] = useState<string>(uuid())
  const [taskHistory, setTaskHistory] = useState<NumHistory[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)
  const historyNumbers = useMemo(() => taskHistory.map(({ num }) => num), [taskHistory])
  const { t } = useTranslation('generics')

  let minNumber = 11
  let maxNumber = 25

  if (difficultyLevel) {
    minNumber = difficulty(difficultyLevel)[0]
    maxNumber = difficulty(difficultyLevel)[1]
  }

  const num = useMemo(() => {
    return generateNumber(minNumber, maxNumber, historyNumbers)
  }, [historyNumbers, minNumber, maxNumber])

  useEmitInstance(onInstanceChange, num ? `${num} = \\square0 + \\square` : null)

  const handleOnCheckAnswerClick = (): void => {
    // Grade each digit on its own: only the total would let 0 tens + 23 ones pass without a split.
    const isCorrect = parseInt(tens) === Math.floor(num / 10) && parseInt(one) === num % 10
    onAnswerStateChange(isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    onAnswerStateChange(null)
    setTens('')
    setOne('')
    setTaskHistory((prevState) => {
      return [{ num }, ...prevState].slice(0, 3)
    })
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const handleOnRemoveButtonClick = (): void => {
    setTens('')
    setOne('')
  }

  const handleOnChange = (value: string, place: Position): void => {
    if (isAllowedValue(value)) {
      onAnswerStateChange(null)
      switch (place) {
        case Position.ONES:
          setOne(value.replace(/[^0-9]+/g, ''))
          break
        case Position.TENS:
          setTens(value.replace(/[^0-9]+/g, ''))
          break
      }
    }
  }
  const onEnterPress = (): void => {
    if (!(tens === '' || one === '')) {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper className="py-5">
      <MultiplicationActivityWrapper>
        <span>{num}</span>
        <div>
          <Equals />
          <Input
            aria-label={t('ariaLabelButtons.insertTen')}
            autoFocus={autofocus}
            isError={answerState === false && num.toString().slice(0, 1) !== tens}
            key={`${inputKey}-tens`}
            maxDigits={1}
            onChange={(value) => handleOnChange(value, Position.TENS)}
            onEnter={onEnterPress}
            ref={inputRef}
            type="text"
            value={tens}
          />
          <span>0</span>
          <Plus />
          <Input
            aria-label={t('ariaLabelButtons.insertOne')}
            isError={answerState === false && num.toString().slice(1) !== one}
            key={`${inputKey}-ones`}
            maxDigits={1}
            onChange={(value) => handleOnChange(value, Position.ONES)}
            onEnter={onEnterPress}
            type="text"
            value={one}
          />
        </div>
      </MultiplicationActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: !(tens === '' || one === ''),
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: handleOnRemoveButtonClick,
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

function generateNumber(min: number, max: number, historyNumbers: number[]): number {
  const num = Math.floor(Math.random() * (max - min + 1) + min)

  if (historyNumbers.includes(num) || num.toString().slice(1) === '0') {
    return generateNumber(min, max, historyNumbers)
  }

  return num
}
